import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { getLinkIcon } from "../../utils/businessCardUtils";
import {
  HiChevronDown,
  HiChevronUp,
  HiOutlineDocumentText,
  HiOutlineDownload,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineUser,
} from "react-icons/hi";
import VCardGenerator from "./VCardGenerator";
import { LinkItem } from "./LinkManager";
import BusinessCardFooter from "./BusinessCardFooter";
import BusinessCardShareButton from "./BusinessCardShareButton";
import UserRegistrationSection from "./UserRegistrationSection";
import BookingLinkButton from "./BookingLinkButton";
import { BusinessCardBooking } from "../../types/BusinessCardTypes";
import BusinessCardContainer from "./BusinessCardContainer";
import BusinessCardEmailLogin from "../pages/businessCardView/BusinessCardEmailLogin";
import ExpandableDescription from "./ExpandableDescription";
import { getFileIcon } from "../../utils/fileIconUtils";
import LanguageSelector from "./LanguageSelector";
import SmartLink from "./SmartLink";
import PreviewSafeAnchor from "./PreviewSafeAnchor";
import PreviewSafeButton from "./PreviewSafeButton";

interface BusinessCardData {
  jobTitle: string;
  description?: string;
  links: LinkItem[];
  uploadedFiles?: Array<{
    fileName: string;
    cdnPath: string;
    displayName: string;
  }>;
  allowVCardDownload?: boolean;
  enableUserRegistration?: boolean;
  showFooter?: boolean;
  booking?: BusinessCardBooking;
}

interface BusinessCardMedicalDisplayProps {
  userData: {
    user_identification: string;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    avatar?: string;
  };
  businessCardData: BusinessCardData;
  companyLogo?: string;
  companyName?: string;
  companyDescription?: string;
  className?: string;
  isPreview?: boolean;
  processId?: string;
  enableUserRegistration?: boolean;
  entityIdentification: string;
  ownerIdentification: string;
  onEmailClick?: () => void;
}

const BusinessCardMedicalDisplay: React.FC<BusinessCardMedicalDisplayProps> = (
  props
) => {
  const {
    userData,
    businessCardData,
    companyLogo,
    companyName,
    companyDescription,
    className = "",
    isPreview = false,
    processId,
    enableUserRegistration = false,
    entityIdentification,
    ownerIdentification,
    onEmailClick,
  } = props;

  const { t } = useTranslation("clientView");
  const [showAllLinks, setShowAllLinks] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);

  const handleFlipToEmail = () => {
    if (isFlipping) return;
    setIsFlipping(true);
    setTimeout(() => {
      setShowEmailLogin(true);
      onEmailClick?.();
    }, 300);
    setTimeout(() => setIsFlipping(false), 600);
  };

  const handleFileDownload = (cdnPath: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = cdnPath;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const primaryLinks = businessCardData.links?.slice(0, 3) || [];
  const additionalLinks = businessCardData.links?.slice(3) || [];
  const hasFiles =
    businessCardData.uploadedFiles && businessCardData.uploadedFiles.length > 0;

  const headerContent = (companyName || companyLogo || companyDescription) && (
    <div className="border-b border-teal-200">
      <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-teal-600 to-emerald-600 text-white relative">
        <div className="absolute top-2 end-2 z-[9999]">
          <LanguageSelector variant="compact" />
        </div>
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-2 left-4 w-3 h-8 bg-white"></div>
          <div className="absolute top-4 left-2 w-8 h-3 bg-white"></div>
          <div className="absolute bottom-4 right-10 w-3 h-8 bg-white"></div>
          <div className="absolute bottom-6 right-8 w-8 h-3 bg-white"></div>
        </div>
        <div className="relative z-10">
          {companyName && (
            <p
              className={`${
                companyName.length > 20 ? "text-xs" : "text-base"
              } font-bold text-white text-start`}
            >
              <bdi>{companyName}</bdi>
            </p>
          )}
          {(companyLogo || companyDescription) && (
            <div className="flex items-start gap-2 mt-1">
              {companyLogo && (
                <div className="w-10 h-10 flex-shrink-0">
                  <img
                    src={companyLogo}
                    alt="Company Logo"
                    className="w-full h-full object-contain bg-white rounded-lg shadow-sm"
                  />
                </div>
              )}
              {companyDescription && (
                <p className="text-xs text-teal-100 italic text-start">
                  <bdi>"{companyDescription}"</bdi>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const mainContent = showEmailLogin ? (
    <div className="flex flex-col gap-3 sm:gap-4">
      <BusinessCardEmailLogin
        entityIdentification={entityIdentification}
        ownerIdentification={ownerIdentification}
        onBackToCard={() => {
          if (isFlipping) return;
          setIsFlipping(true);
          setTimeout(() => setShowEmailLogin(false), 300);
          setTimeout(() => setIsFlipping(false), 600);
        }}
      />
    </div>
  ) : (
    <div className="flex flex-col gap-3 sm:gap-4">
      <div className="pb-3 border-b border-teal-100 bg-gradient-to-r from-teal-50 to-emerald-50 p-3 rounded-lg">
        <div className="flex items-center gap-4">
          <div className="relative">
            {userData.avatar ? (
              <img
                src={userData.avatar}
                alt={`${userData.first_name} ${userData.last_name}`}
                className="w-11 h-11 ring-2 ring-teal-200 rounded-lg object-cover"
              />
            ) : (
              <div className="w-11 h-11 ring-2 ring-teal-200 rounded-lg bg-teal-50 flex items-center justify-center">
                {userData.first_name?.[0] || userData.last_name?.[0] ? (
                  <span className="text-base font-bold text-teal-700 uppercase">
                    {userData.first_name?.[0]}
                    {userData.last_name?.[0]}
                  </span>
                ) : (
                  <HiOutlineUser className="w-6 h-6 text-teal-500" />
                )}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-lg font-bold text-teal-900 break-words">
              <bdi>
                {userData.first_name} {userData.last_name}
              </bdi>
            </h1>
            <p className="text-sm font-semibold text-teal-600 break-words text-start">
              <bdi>{businessCardData.jobTitle}</bdi>
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <PreviewSafeAnchor
          href={`mailto:${userData.email}`}
          className="grid grid-cols-[auto_1fr] gap-2 sm:gap-3 w-full p-2.5 sm:p-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg hover:from-teal-600 hover:to-emerald-600 transition-all duration-200 shadow-sm hover:shadow-sm items-center"
        >
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg grid place-items-center flex-shrink-0">
            <HiOutlineMail className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p
              dir="auto"
              className="text-xs text-teal-100 font-medium text-start"
            >
              {t("key98")}
            </p>
            <p
              className="font-semibold text-xs sm:text-sm truncate"
              title={userData.email}
            >
              <bdi>{userData.email}</bdi>
            </p>
          </div>
        </PreviewSafeAnchor>

        {userData.phone && (
          <PreviewSafeAnchor
            href={`tel:${userData.phone}`}
            className="flex items-center gap-2 sm:gap-3 w-full p-2.5 sm:p-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-all duration-200 shadow-sm hover:shadow-sm"
          >
            <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <HiOutlinePhone className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p
                dir="auto"
                className="text-xs text-emerald-100 font-medium text-start"
              >
                {t("businessCard.phone").toUpperCase()}
              </p>
              <p className="font-semibold text-xs sm:text-sm">
                <bdi>{userData.phone}</bdi>
              </p>
            </div>
          </PreviewSafeAnchor>
        )}
      </div>

      {businessCardData.description && (
        <div className="p-3 bg-white border-l-4 border-teal-400 rounded-r-lg shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
            <p className="text-xs font-semibold text-teal-700 uppercase tracking-wide text-start">
              {t("key106").toUpperCase()}
            </p>
          </div>

          <ExpandableDescription
            content={businessCardData.description}
            maxLength={150}
            maxLines={5}
            textClassName="text-sm text-gray-700 leading-relaxed"
            buttonClassName="mt-2 inline-flex items-center text-xs font-medium text-teal-600 hover:text-teal-700 transition-colors duration-200"
            expandText={t("key97") as string}
            collapseText={t("key107") as string}
          />
        </div>
      )}

      {primaryLinks.length > 0 && (
        <div className="space-y-2">
          {primaryLinks.map((link, index) => (
            <SmartLink
              key={index}
              href={link.link}
              fallbackHref={link.fallbackLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 sm:gap-3 p-2 bg-teal-50 hover:bg-teal-100 border border-teal-200 hover:border-teal-300 rounded-lg transition-all duration-200 group"
            >
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm">
                {getLinkIcon(link, "text-teal-600")}
              </div>
              <p className="flex-1 text-sm font-medium text-teal-900 group-hover:text-teal-600 truncate text-start">
                {link.label}
              </p>
              →
            </SmartLink>
          ))}
        </div>
      )}

      {additionalLinks.length > 0 && (
        <div>
          {showAllLinks && (
            <div className="space-y-2 mb-2">
              {additionalLinks.map((link, index) => (
                <SmartLink
                  key={index + 3}
                  href={link.link}
                  fallbackHref={link.fallbackLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 sm:gap-3 p-2 bg-teal-50 hover:bg-teal-100 border border-teal-200 hover:border-teal-300 rounded-lg transition-all duration-200 group"
                >
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm">
                    {getLinkIcon(link, "text-teal-600")}
                  </div>
                  <p className="flex-1 text-sm font-medium text-teal-900 truncate group-hover:text-teal-600 text-start">
                    {link.label}
                  </p>
                  →
                </SmartLink>
              ))}
            </div>
          )}
          <PreviewSafeButton
            onClick={() => setShowAllLinks(!showAllLinks)}
            className="flex items-center justify-center w-full p-2 text-sm text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg transition-colors duration-200"
            dir="auto"
          >
            <span className="me-1 text-start">
              {showAllLinks
                ? t("businessCard.showLess")
                : t("businessCard.showMoreLinks", {
                    count: additionalLinks.length,
                  })}
            </span>
            {showAllLinks ? (
              <HiChevronUp className="w-4 h-4" />
            ) : (
              <HiChevronDown className="w-4 h-4" />
            )}
          </PreviewSafeButton>
        </div>
      )}

      {hasFiles && (
        <div>
          <PreviewSafeButton
            onClick={() => setShowFiles(!showFiles)}
            className="flex items-center justify-between w-full p-2 text-sm text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded-lg transition-colors duration-200"
            dir="auto"
          >
            <div className="flex items-center gap-2">
              <HiOutlineDocumentText className="w-4 h-4" />
              <span className="text-start">
                {t("businessCard.files", {
                  count: businessCardData.uploadedFiles?.length || 0,
                })}
              </span>
            </div>
            {showFiles ? (
              <HiChevronUp className="w-4 h-4" />
            ) : (
              <HiChevronDown className="w-4 h-4" />
            )}
          </PreviewSafeButton>

          {showFiles && businessCardData.uploadedFiles && (
            <div className="mt-2 space-y-1">
              {businessCardData.uploadedFiles.map((file, index) => (
                <PreviewSafeButton
                  key={index}
                  onClick={() =>
                    handleFileDownload(file.cdnPath, file.fileName)
                  }
                  className="w-full flex items-center gap-2 p-2 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors duration-200 text-left group"
                  dir="auto"
                >
                  <span className="text-sm">{getFileIcon(file.fileName)}</span>
                  <p className="flex-1 text-sm font-medium text-gray-900 truncate group-hover:text-orange-700 text-start">
                    {file.displayName}
                  </p>
                  <HiOutlineDownload className="w-4 h-4 text-gray-400 group-hover:text-orange-500" />
                </PreviewSafeButton>
              ))}
            </div>
          )}
        </div>
      )}

      {(() => {
        const showSaveContact = businessCardData.allowVCardDownload === true;
        const showShare = !!processId;
        if (!showSaveContact && !showShare) return null;

        return (
          <div
            className={`grid gap-2 ${
              showSaveContact && showShare ? "grid-cols-2" : "grid-cols-1"
            }`}
          >
            {showSaveContact && (
              <VCardGenerator
                data={{
                  firstName: userData.first_name,
                  lastName: userData.last_name,
                  jobTitle: businessCardData.jobTitle,
                  email: userData.email,
                  phone: userData.phone,
                  description: businessCardData.description,
                  links: businessCardData.links,
                  avatar: userData.avatar,
                }}
                buttonColor="teal"
              />
            )}
            {showShare && (
              <BusinessCardShareButton
                processId={processId}
                size="md"
                variant="secondary"
                logoUrl={companyLogo}
              />
            )}
          </div>
        );
      })()}

      <BookingLinkButton
        booking={businessCardData.booking}
        entityIdentification={entityIdentification}
        memberId={userData.user_identification}
      />

      {enableUserRegistration && processId && entityIdentification && (
        <UserRegistrationSection
          processId={processId}
          entityData={{
            entity_identification: entityIdentification,
            entity_name: companyName,
            entity_logo: companyLogo,
          }}
          theme="classic"
          size="sm"
          onEmailLogin={handleFlipToEmail}
        />
      )}
    </div>
  );

  const frontContent = (
    <div className="w-full bg-gradient-to-b from-teal-50 to-white rounded-xl overflow-hidden">
      <div className="w-full min-h-full relative">
        {headerContent && (
          <div className="row-start-1 row-end-2">{headerContent}</div>
        )}

        <div className="row-start-2 row-end-3">
          <div className="px-3 sm:px-4 py-3 sm:py-4">{mainContent}</div>
        </div>

        <div className="row-start-3 row-end-4">
          <BusinessCardFooter
            showFooter={businessCardData.showFooter as boolean}
            theme="medical"
          />
        </div>
      </div>
    </div>
  );

  return (
    <BusinessCardContainer
      isPreview={isPreview}
      className={`w-full h-auto min-h-0 ${className}`}
      frontContent={frontContent}
      isFlipping={isFlipping}
      showEmailLogin={showEmailLogin}
    />
  );
};

export default BusinessCardMedicalDisplay;
